import React, { useEffect, useRef, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MODEL_TYPE_CAREER_PREFERENCE,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import {
  getCookie,
  getEnneagramBannerImage,
  getPreferredAcademicDegreesImage,
  getIndustryBannerImage,
} from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ExpandableSection from "../../components/expandable-collapsible/component";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const CareerPreferenceReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();
  // const [content, setContent] = useState(null);
  const [contentKey, setContentKey] = useState("");
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const bannerImage = getEnneagramBannerImage();
  const result1ImageText = "career_academic";
  const academicdegreeImage =
    getPreferredAcademicDegreesImage(result1ImageText);
  const traitCode = queryParams.get("code");
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
  const industryImage = getIndustryBannerImage(traitCode);
  const { t } = useTranslation();
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_CAREER_PREFERENCE[currentLocale] ||
            MODEL_TYPE_CAREER_PREFERENCE[LOCALE.EN],
          contentKey: traitCode,
        });
        if (contentResponse) {
          const { content } = contentResponse;

          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const personalityGroup = currentAssessmentAttemptData?.personality?.role;
  const personalityName = currentAssessmentAttemptData?.personality?.name;

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const descriptiveCardColorMap = [
    { bg: "bg-blue-50", text: "text-blue-800" },
    { bg: "bg-purple-50", text: "text-purple-800" },
    { bg: "bg-amber-50", text: "text-amber-800" },
    { bg: "bg-green-50", text: "text-green-800" },
    { bg: "bg-pink-50", text: "text-pink-800" },
    { bg: "bg-sky-50", text: "text-sky-800" },
    { bg: "bg-orange-50", text: "text-orange-800" },
    { bg: "bg-emerald-50", text: "text-emerald-800" },
    { bg: "bg-violet-50", text: "text-violet-800" },
  ];

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
        id="career-preference-report-take-test-button"
      >
        {t("report.career_preference_precontent.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            {/* Title & Button Wrapper */}
            <div
              className="flex flex-col md:flex-row items-start justify-between gap-4 pr-10 md:pr-16"
              id={reportType}
            >
              {/* Title */}
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DescriptiveCard = ({ heading, bgClr, overview, textClr }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Check if the screen is mobile size
    useEffect(() => {
      const checkIsMobile = () => {
        setIsMobile(window.innerWidth < 768); // 768px is the standard md breakpoint
      };

      checkIsMobile();
      window.addEventListener("resize", checkIsMobile);

      return () => window.removeEventListener("resize", checkIsMobile);
    }, []);

    const toggleExpand = () => {
      if (isMobile) {
        setIsExpanded(!isExpanded);
      }
    };

    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div
            className="flex items-start justify-between gap-2 mb-2 cursor-pointer md:cursor-default"
            onClick={toggleExpand}
          >
            <div className="flex items-start gap-2 p-2">
              <h2
                className={`text-lg sm:text-xl ${textClr} font-semibold flex items-center`}
              >
                {heading}
              </h2>
            </div>

            {/* Only show toggle icon on mobile */}
            {isMobile && (
              <span
                className={`transform transition-transform duration-300 my-auto ${
                  !isExpanded ? "-rotate-90" : "rotate-90"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </span>
            )}
          </div>
        )}

        {/* For desktop: always show content */}
        <div className="hidden md:block">
          {overview && (
            <p className={`text-lg text-gray-700 mt-4`}>{overview}</p>
          )}
        </div>

        {/* For mobile: show content conditionally */}
        <div
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isExpanded ? "max-h-96" : "max-h-0"
          }`}
        >
          {overview && (
            <p className={`text-lg text-gray-700 mt-4`}>{overview}</p>
          )}
        </div>
      </div>
    );
  };

  const Grid = ({ commonJobTitles = [] }) => {
    const totalItems = commonJobTitles.length;

    const splitIntoRows = () => {
      const rows = [];
      for (let i = 0; i < totalItems; i += 3) {
        rows.push(commonJobTitles.slice(i, i + 3));
      }
      return rows;
    };

    const rows = splitIntoRows();

    return (
      <div className="mt-4">
        {rows.map((row, rowIndex) => {
          const isLastRow = rowIndex === rows.length - 1;

          let gridClass = "grid grid-cols-1 md:grid-cols-3 gap-6 mb-6";

          if (isLastRow) {
            if (row.length === 1) {
              gridClass = "grid grid-cols-1 gap-6 mb-6";
            } else if (row.length === 2) {
              gridClass = "grid grid-cols-1 md:grid-cols-2 gap-6 mb-6";
            }
          }

          return (
            <div key={rowIndex} className={gridClass}>
              {row.map((item, index) => {
                const actualIndex = rowIndex * 3 + index;

                return (
                  <div key={index} className="flex flex-col h-full">
                    <DescriptiveCard
                      heading={item?.Heading}
                      overview={item?.Description}
                      bgClr={
                        descriptiveCardColorMap[
                          actualIndex % descriptiveCardColorMap.length
                        ].bg
                      }
                      textClr={
                        descriptiveCardColorMap[
                          actualIndex % descriptiveCardColorMap.length
                        ].text
                      }
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const DescriptiveCard2 = ({ heading, bgClr, overview, textClr }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full mx-2`}
      >
        {heading && (
          <div className="flex items-start justify-between gap-2 mb-2 cursor-pointer md:cursor-default">
            <div className="flex items-start gap-2">
              <h2
                className={`text-lg sm:text-xl ${textClr} font-semibold flex items-center`}
              >
                {heading}
              </h2>
            </div>
          </div>
        )}

        {overview && <p className={`text-lg text-gray-700 mt-4`}>{overview}</p>}
      </div>
    );
  };

  const SliderGrid = ({ commonJobTitles = [] }) => {
    const [isMobile, setIsMobile] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const sliderRef = useRef(null);

    useEffect(() => {
      const checkIsMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };

      checkIsMobile();
      window.addEventListener("resize", checkIsMobile);
      return () => window.removeEventListener("resize", checkIsMobile);
    }, []);

    const sliderSettings = {
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      draggable: true,
      swipeToSlide: true,
      centerMode: true,
      centerPadding: "15%",
      beforeChange: (current, next) => setActiveSlide(next),
      responsive: [
        {
          breakpoint: 640,
          settings: {
            centerPadding: "0%",
          },
        },
      ],
    };
    const rows = [];
    for (let i = 0; i < commonJobTitles.length; i += 3) {
      rows.push(commonJobTitles.slice(i, i + 3));
    }
    return (
      <div className="mt-8">
        <div className="hidden md:flex flex-col">
          {rows.map((rowItems, rowIndex) => {
            const isLastRow = rowIndex === rows.length - 1;
            let gridClass = "grid grid-cols-1 md:grid-cols-3 gap-6 mb-6";

            if (isLastRow) {
              if (rowItems.length === 1) {
                gridClass = "grid grid-cols-1 gap-6 mb-6";
              } else if (rowItems.length === 2) {
                gridClass = "grid grid-cols-1 md:grid-cols-2 gap-6 mb-6";
              }
            }

            return (
              <div key={rowIndex} className={gridClass}>
                {rowItems.map((item, index) => (
                  <DescriptiveCard2
                    key={index}
                    heading={item?.Heading}
                    overview={item?.Description}
                    bgClr={
                      descriptiveCardColorMap[
                        (rowIndex * 3 + index) % descriptiveCardColorMap.length
                      ].bg
                    }
                    textClr={
                      descriptiveCardColorMap[
                        (rowIndex * 3 + index) % descriptiveCardColorMap.length
                      ].text
                    }
                  />
                ))}
              </div>
            );
          })}
        </div>

        {isMobile && (
          <>
            <Slider
              {...sliderSettings}
              ref={sliderRef}
              className="slider-grid mb-6"
            >
              {commonJobTitles.map((item, index) => (
                <div key={`mobile-${index}`} className="">
                  <div className="shadow-sm bg-white h-full">
                    <DescriptiveCard2
                      heading={item?.Heading}
                      overview={item?.Description}
                      bgClr={
                        descriptiveCardColorMap[
                          index % descriptiveCardColorMap.length
                        ].bg
                      }
                      textClr={
                        descriptiveCardColorMap[
                          index % descriptiveCardColorMap.length
                        ].text
                      }
                    />
                  </div>
                </div>
              ))}
            </Slider>

            {/* Bullet Navigation */}
            <div className="flex justify-center items-center space-x-2 mt-4 mb-6">
              {commonJobTitles.map((_, index) => (
                <button
                  key={`nav-bullet-${index}`}
                  onClick={() => {
                    if (sliderRef.current) {
                      sliderRef.current.slickGoTo(index);
                    }
                  }}
                  className={`transition-all duration-300 rounded-full focus:outline-none ${
                    activeSlide === index
                      ? "w-6 h-2 bg-[#805fe6]"
                      : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };
  const industryOverview = contentData?.industry_overview;

  const commonJobTitles = contentData?.common_job_titles;

  const skill = contentData?.required_skills;

  const commonTasks = contentData?.common_tasks;

  const IndustryOverview = () => {
    const [activeSection, setActiveSection] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkIsMobile = () => {
        setIsMobile(window.innerWidth < 768); // 768px is the standard md breakpoint
      };

      checkIsMobile();
      window.addEventListener("resize", checkIsMobile);

      return () => window.removeEventListener("resize", checkIsMobile);
    }, []);

    const handleSectionClick = (index) => {
      if (isMobile) {
        setActiveSection(activeSection === index ? null : index);
      }
    };

    return (
      <div className="flex flex-col md:flex-row gap-8 mt-6">
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-4 text-gray-700">
            {industryOverview?.RootHeading}
          </h2>

          <div className="hidden md:grid md:grid-cols-3 gap-6 mb-6">
            {industryOverview?.Content?.map((item, index) => (
              <div key={`desktop-${index}`} className="mb-4">
                <h3 className="text-lg font-semibold text-[#805fe6]">
                  {item?.Heading}
                </h3>
                <p className="text-gray-700">{item?.Description}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 mb-6 md:hidden">
            {industryOverview?.Content?.map((item, index) => (
              <div key={`mobile-${index}`} className="mb-2 overflow-hidden">
                <div
                  className="flex justify-between items-center p-3 cursor-pointer"
                  onClick={() => handleSectionClick(index)}
                >
                  <h3 className="text-lg font-semibold text-[#805fe6]">
                    {item?.Heading}
                  </h3>
                  <span
                    className={`transform transition-transform duration-300 ${
                      activeSection !== index ? "-rotate-90" : "rotate-90"
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="50"
                      height="95"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="grey"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="feather feather-chevron-left w-6 h-12 sm:w-10 sm:h-16 md:w-[100px] md:h-[190px]"
                    >
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </span>
                </div>

                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    activeSection === index ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="p-3">
                    <p className="text-gray-700 mb-2">{item?.Description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const SliderIndustryOverview = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const industrySliderRef = useRef(null);

    useEffect(() => {
      const checkIsMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };

      checkIsMobile();
      window.addEventListener("resize", checkIsMobile);

      return () => window.removeEventListener("resize", checkIsMobile);
    }, []);

    const sliderSettings = {
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      draggable: true,
      swipeToSlide: true,
      centerMode: true,
      centerPadding: "15%",
      beforeChange: (current, next) => setActiveSlide(next),
      responsive: [
        {
          breakpoint: 640,
          settings: {
            centerPadding: "0%",
          },
        },
      ],
    };

    return (
      <div className="flex flex-col md:flex-row gap-8 mt-6">
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-4 text-gray-700">
            {industryOverview?.RootHeading}
          </h2>
          <div className="hidden md:grid md:grid-cols-3 gap-6 mb-6">
            {industryOverview?.Content?.map((item, index) => (
              <div key={index} className="mb-4">
                <h3 className="text-lg font-semibold text-[#805fe6]">
                  {item?.Heading}
                </h3>
                <p className="text-gray-700">{item?.Description}</p>
              </div>
            ))}
          </div>
          {isMobile && (
            <>
              <Slider
                {...sliderSettings}
                ref={industrySliderRef}
                className="industry-slider mb-6"
              >
                {industryOverview?.Content?.map((item, index) => (
                  <div key={`mobile-${index}`} className="px-2">
                    <div className=" shadow-sm bg-white h-full">
                      <div className="">
                        <h3 className="text-lg font-semibold text-[#805fe6] mb-3">
                          {item?.Heading}
                        </h3>
                        <p className="text-gray-700 mb-2">
                          {item?.Description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
              <div className="flex justify-center items-center space-x-2 mt-4 mb-6">
                {industryOverview?.Content?.map((_, index) => (
                  <button
                    key={`nav-bullet-${index}`}
                    onClick={() => {
                      if (industrySliderRef.current) {
                        industrySliderRef.current.slickGoTo(index);
                      }
                    }}
                    className={`transition-all duration-300 rounded-full focus:outline-none ${
                      activeSlide === index
                        ? "w-6 h-2 bg-[#805fe6]"
                        : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                    }`}
                    aria-label={`Go to industry section ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner
                      ///tush
                      title={t(
                        "report.career_preference_precontent.test_results_title"
                      )}
                      color="blue"
                    />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full md:px-8 md:py-6">
                    <div className="items-start">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h2 className="text-gray-700 text-3xl mb-4 font-semibold">
                            {contentData?.industry}
                          </h2>
                          <p className="text-gray-700 text-base mt-6">
                            {contentData?.intro}
                          </p>
                        </div>

                        {/* Image Section - Adjusts Dynamically */}
                        {industryImage ? (
                          <div className="flex justify-center items-center my-6">
                            <img
                              src={industryImage}
                              alt="Industry"
                              className="w-[90%] h-auto md:w-[80%] md:h-auto object-contain"
                            />
                          </div>
                        ) : null}
                      </div>
                      <SliderIndustryOverview />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={commonJobTitles?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full  md:px-8 md:py-6">
                    <p className={`text-lg text-gray-700`}>
                      {commonJobTitles?.Content?.flavor_text}
                    </p>
                    <SliderGrid
                      commonJobTitles={
                        commonJobTitles?.Content?.common_job_titles
                      }
                    />
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner title={skill?.RootHeading} color="red" />
                  }
                >
                  <div className="w-full  md:px-8 md:py-6">
                    <p className={`text-lg text-gray-700`}>
                      {skill?.Content?.flavor_text}
                    </p>
                    <SliderGrid
                      commonJobTitles={skill?.Content?.required_skills}
                    />
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={commonTasks?.RootHeading}
                      color="green"
                    />
                  }
                >
                  <div className="w-full  md:px-8 md:py-6">
                    <p className={`text-lg text-gray-700`}>
                      {commonTasks?.Content?.flavor_text}
                    </p>
                    <SliderGrid
                      commonJobTitles={commonTasks?.Content?.common_tasks}
                    />
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        contentData?.preferred_academic_degrees?.RootHeading
                      }
                      color="indigo"
                    />
                  }
                >
                  {/* {renderHeroBanner(contentData.header, "blue")} */}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 items-center">
                      {/* Text Section - Adjusts Based on Content */}
                      <div className="flex-1">
                        <p className="text-gray-700">
                          {contentData?.preferred_academic_degrees?.Content}
                        </p>
                      </div>
                      {/* Image Section - Adjusts Dynamically */}
                      <div className="flex items-cente justify-center ">
                        {/* Image will be inserted here */}

                        <img
                          src={academicdegreeImage}
                          alt="PreferredAcademicDegreeImage"
                          className="w-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <HeroBanner
                  title={t(
                    "report.career_preference_precontent.career_assessment"
                  )}
                  color="yellow"
                  buttonNeeded={true}
                  onClick={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                    );
                  }}
                />
              </section>

              {/* <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <HeroBanner
              title={`Use AI Career Guidance Tool`}
              color="pink"
              buttonNeeded={true}
            />
          </section> */}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CareerPreferenceReport;
